import React from 'react';
import {
  Switch as MuiSwitch,
  SwitchProps as MuiSwitchProps,
  FormLabel,
  Typography,
  TypographyVariant,
  TypographyPropsVariantOverrides,
} from '@mui/material';
import { OverridableStringUnion } from '@mui/types';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';
import { Box } from '../Box';

export interface SwitchProps
  extends Omit<MuiSwitchProps, keyof PounceStyleProps>, PounceStyleProps {
  /** Whether the checkbox is currently disabled */
  disabled?: boolean;

  /**  Whether the input has an invalid value or not */
  invalid?: boolean;

  /** The label associated with the Switch. Appears on the right. */
  label?: string;

  /** The position of the label. */
  labelPosition?: 'left' | 'right';

  /** The text to show when the switch is checked */
  checkedText?: string;

  /** The text to show when the switch is unchecked */
  uncheckedText?: string;

  /** The size of the switch. Defaults to `medium`. */
  size?: 'medium' | 'large';

  /** The Typography variant used for the label. Defaults to `body/md`. */
  labelVariant?: OverridableStringUnion<
    TypographyVariant | 'inherit',
    TypographyPropsVariantOverrides
  >;

  /** Additional styles to apply (combined with pounce props) */
  sx?: MuiSwitchProps['sx'];
}

/**
 * Switch component built on top of MUI Switch.
 *
 * A Switch is a typical Checkbox with a different UI. It's mainly used for settings pages, when
 * enabling or disabling feature.
 *
 * Provides all the functionality from the original Pounce Switch while leveraging
 * MUI's accessibility and interaction patterns.
 */
export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  {
    checked,
    label,
    labelPosition = 'left',
    checkedText = '',
    uncheckedText = '',
    disabled,
    invalid,
    readOnly,
    hidden,
    id,
    size = 'medium',
    labelVariant = 'body/md',
    sx,
    ...rest
  },
  ref,
) {
  const generatedId = React.useId();
  const inputId = id || generatedId;

  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);

  // Size-specific styles (large is bigger than default medium)
  const sizeStyles =
    size === 'large'
      ? {
          width: 72, // 1.33x of 54
          height: 36, // 1.33x of 27
          '& .MuiSwitch-switchBase': {
            top: 6,
            left: 6,
            '&.Mui-checked': {
              transform: 'translateX(36px)', // Adjusted for larger track
            },
          },
          '& .MuiSwitch-thumb': {
            width: 24, // 1.5x of 16
            height: 24,
          },
          '& .MuiSwitch-track': {
            borderRadius: 18, // Half of height (36/2)
          },
        }
      : {};

  // Combine pounce-generated styles with size and user sx
  const combinedSx = {
    ...pounceStyles,
    ...sizeStyles,
    ...(sx || {}),
  } as MuiSwitchProps['sx'];

  if (!label && !(otherProps['aria-label'] || otherProps['aria-labelledby'])) {
    // eslint-disable-next-line no-console
    console.error(
      'The `label` prop was omitted without providing an `aria-label` or `aria-labelledby` attribute',
    );
  }

  // Adjust text positioning based on size
  const textOffset = size === 'large' ? '10px' : '7px';
  const textFontSize = size === 'large' ? '12px' : '10px';

  const switchElement = (
    <Box position="relative" display="inline-flex" alignItems="center">
      <MuiSwitch
        ref={ref}
        checked={checked}
        disabled={disabled}
        readOnly={readOnly}
        color={invalid ? 'error' : 'primary'}
        sx={combinedSx}
        id={inputId}
        slotProps={{
          input: {
            'aria-invalid': invalid,
            'aria-readonly': readOnly,
          },
        }}
        {...otherProps}
      />
      <Box
        position="absolute"
        left={checked ? textOffset : undefined}
        right={!checked ? textOffset : undefined}
        fontWeight="bold"
        fontSize={textFontSize}
        userSelect="none"
        pointerEvents="none"
        sx={{
          color: theme => theme.palette.text.primary,
        }}
      >
        {checked ? checkedText : uncheckedText}
      </Box>
    </Box>
  );

  return (
    <Box
      as="span"
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      cursor="pointer"
      fontSize="medium"
      aria-disabled={disabled}
      hidden={hidden}
      aria-hidden={hidden}
    >
      {label && labelPosition === 'left' && (
        <FormLabel
          htmlFor={inputId}
          sx={{
            userSelect: 'none',
            mr: 2,
            cursor: 'pointer',
            flex: 1,
            color: 'inherit',
            fontSize: 'inherit',
            fontWeight: 'inherit',
          }}
        >
          <Typography component="span" variant={labelVariant} sx={{ fontWeight: 'inherit' }}>
            {label}
          </Typography>
        </FormLabel>
      )}
      {switchElement}
      {label && labelPosition === 'right' && (
        <FormLabel
          htmlFor={inputId}
          sx={{
            userSelect: 'none',
            ml: 2,
            cursor: 'pointer',
            flex: 1,
            color: 'inherit',
            fontSize: 'inherit',
          }}
        >
          <Typography component="span" variant={labelVariant} sx={{ fontWeight: 'inherit' }}>
            {label}
          </Typography>
        </FormLabel>
      )}
    </Box>
  );
});
